import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardGenerateContinueByPlanUX from "./CardGenerateContinueByPlanUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import {
  State,
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  FilterSearchType,
  // TaskOptions,
  TableHeaderType,
} from "./sequence/GenerateContinueByPlan";

// Serializer
import GenDrugContinueInterfaceLogSerializer from "issara-sdk/types/GenDrugContinueIssueLogSerializer_apps_TPD";
import { Checkbox } from "semantic-ui-react";

type CardGenerateContinueByPlanProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  GenerateContinueByPlanSequence: State["GenerateContinueByPlanSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardGenerateContinueByPlan = (props: CardGenerateContinueByPlanProps) => {
  const intl = useIntl();

  // useState
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "GenerateContinueByPlan", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.GenerateContinueByPlanSequence?.continueList || []).map(
        (item: any) => item.id
      );

      setSelectedItems(newData);
    } else {
      setSelectedItems([]);
    }
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;

    props.setProp(`GenerateContinueByPlanSequence.searchContinue.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`GenerateContinueByPlanSequence.searchContinue.created_date`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`GenerateContinueByPlanSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "GenerateContinueByPlan",
      action: "SEARCH",
      [key]: value,
    });

    setSelectedItems([]);
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "GenerateContinueByPlan",
      action: "SEARCH",
      page: 0,
    });

    setSelectedItems([]);
  };

  const handleGenerate = () => {
    props.runSequence({
      sequence: "GenerateContinueByPlan",
      action: "GENERATE",
      selectedItems,
    });

    setSelectedItems([]);
  };

  const handleDisabledGenerate = useMemo(() => {
    if (selectedItems.length === 0) return true;
  }, [selectedItems]);

  // useMemo
  const continueList = useMemo(() => {
    return (props.GenerateContinueByPlanSequence?.continueList || []).map((item: any) => {
      return {
        ...item,
        _checked: columnTableCenter(
          <Checkbox
            checked={selectedItems.includes(item.id)}
            // disabled={}
            onChange={(e: any, v: any) => {
              if (v.checked) {
                setSelectedItems([...selectedItems, item.id]);
              } else {
                let newData = selectedItems.filter((key: any) => key !== item.id);
                setSelectedItems(newData);
              }
            }}
          />,
          { padding: "10px" }
        ),
        ...tableHeaders.reduce((data: Partial<Record<keyof any, ReactNode>>, header) => {
          const key = header.accessor;

          // if (key === "generate_date") {
          //   data[key] = columnTableCenter(dateToStringBE(item[key]));
          // }  else {
          //   data[key] = columnTableCenter(item[key]);
          // }
          data[key] = columnTableCenter(item[key]);

          return data;
        }, {}),
      };
    });
  }, [selectedItems, props.GenerateContinueByPlanSequence?.continueList, tableHeaders]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardGenerateContinueByPlanUX
        onChangeData={handleChangeData}
        onChangeDate={handleChangeDate}
        searchContinue={props.GenerateContinueByPlanSequence?.searchContinue}
        // dropdown
        divisionOptions={props.masterOptions?.division || []}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedItems.length ===
                    (props.GenerateContinueByPlanSequence?.continueList || []).length &&
                  (props.GenerateContinueByPlanSequence?.continueList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        continueList={continueList}
        paginatedItems={props.GenerateContinueByPlanSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // disabled={}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title="Search"
            name="SEARCH"
            size="medium"
          />
        }
        generateButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleGenerate}
            disabled={handleDisabledGenerate}
            // data
            paramKey={`${CARD_NAME}_GENERATE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_GENERATE`]}
            // config
            color="green"
            title="Generate"
            name="GENERATE"
            size="medium"
          />
        }
      />
    </>
  );
};

CardGenerateContinueByPlan.displayName = "CardGenerateContinueByPlan";

export default React.memo(CardGenerateContinueByPlan);

const tableHeaders: TableHeaderType<any>[] = [
  {
    Header: "ID",
    accessor: "id",
    // width: 150,
  },
  {
    Header: "HN",
    accessor: "hn",
    // width: 120,
  },
  {
    Header: "Code",
    accessor: "code",
  },
  {
    Header: "Drug",
    accessor: "drug",
  },
  {
    Header: "Division",
    accessor: "division",
  },
];
